"use-client"

import { GetWebPageData, getWebPage } from "@/api";
import { client } from "@/api/client.gen";
import { Request } from "../components/CRUD";
import { FormEvent, useEffect, useState } from "react";
import { appState } from "@/app/appState";
import { page_artifact } from "@/app/typing/artifacts";
import { ForwardRefEditor } from "@/components/richtext/ForwardRefEditor";
import {
    codeBlockPlugin,
    codeMirrorPlugin,
    headingsPlugin,
    listsPlugin,
    quotePlugin,
    tablePlugin,
    thematicBreakPlugin,
} from "@mdxeditor/editor";
import { observer } from "mobx-react-lite";
import { PublicPageShell } from "@/components/PublicPageShell";

export default observer(function SignUp({ id }: { id: number }) {
    const [loading, setLoading] = useState<boolean>(false);
    const [pageArtifact, setPageArtifact] = useState<page_artifact>();
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [organisation, setOrganisation] = useState("");
    const [message, setMessage] = useState("");
    const [website, setWebsite] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const getArtifact = Request<GetWebPageData, any>(
        getWebPage,
        () => ({
            body: {
                id: id!,
            },
        }),
        setLoading,
        (data: any) => {
            setPageArtifact(data.artifact);
        },
        () => {
            setPageArtifact(undefined);
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("marketing.errorGetPage"),
            });
        },
        () => id != undefined && id > 0,
    );

    useEffect(() => {
        getArtifact();
    }, [id]);

    const submitContact = async (event: FormEvent) => {
        event.preventDefault();
        setFormError(null);
        if (!name.trim() || !email.trim() || !message.trim()) {
            setFormError(appState.t("contact.form.required"));
            return;
        }
        setSending(true);
        try {
            const response = await client.post({
                url: "/api/contact",
                body: {
                    name: name.trim(),
                    email: email.trim(),
                    message: message.trim(),
                    organisation: organisation.trim() || undefined,
                    website: website.trim() || undefined,
                },
                headers: { "Content-Type": "application/json" },
            });
            if (response.error) {
                setFormError(appState.t("contact.form.error"));
                return;
            }
            setSent(true);
        } catch {
            setFormError(appState.t("contact.form.error"));
        } finally {
            setSending(false);
        }
    };

    return (
        <PublicPageShell
            footerLanguageSwitcher
            contentClassName="justify-center items-center p-3"
        >
            {loading ? null : (
                <div className="public-card flex flex-col gap-4">
                    <ForwardRefEditor
                        markdown={pageArtifact?.content ?? ""}
                        readOnly={true}
                        plugins={[
                            headingsPlugin(),
                            listsPlugin(),
                            quotePlugin(),
                            thematicBreakPlugin(),
                            tablePlugin(),
                            codeBlockPlugin({ defaultCodeBlockLanguage: "" }),
                            codeMirrorPlugin({ codeBlockLanguages: { "": "Plain Text" } }),
                        ]}
                        contentEditableClassName="w-full serif"
                    />
                    {sent ? (
                        <p className="l">{appState.t("contact.form.success")}</p>
                    ) : (
                        <form onSubmit={submitContact} className="relative flex flex-col gap-1 items-start">
                            <label htmlFor="contact-name" className="l mt-1">
                                {appState.t("common.name")}
                            </label>
                            <input
                                id="contact-name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="input"
                                required
                                maxLength={200}
                                autoComplete="name"
                            />
                            <label htmlFor="contact-email" className="l mt-1">
                                {appState.t("common.email")}
                            </label>
                            <input
                                id="contact-email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="input"
                                required
                                maxLength={320}
                                autoComplete="email"
                            />
                            <label htmlFor="contact-organisation" className="l mt-1">
                                {appState.t("contact.form.organisation")}
                            </label>
                            <input
                                id="contact-organisation"
                                value={organisation}
                                onChange={(e) => setOrganisation(e.target.value)}
                                className="input"
                                maxLength={200}
                                autoComplete="organization"
                            />
                            <label htmlFor="contact-message" className="l mt-1">
                                {appState.t("contact.form.message")}
                            </label>
                            <textarea
                                id="contact-message"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                className="textarea min-h-[8rem]"
                                required
                                maxLength={5000}
                            />
                            <div className="absolute -left-[10000px] h-0 w-0 overflow-hidden" aria-hidden="true">
                                <label htmlFor="contact-website">Website</label>
                                <input
                                    id="contact-website"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={website}
                                    onChange={(e) => setWebsite(e.target.value)}
                                />
                            </div>
                            {formError ? <p className="error-text mt-1">{formError}</p> : null}
                            <button
                                type="submit"
                                disabled={sending}
                                className="btn btn-primary btn-block mt-1"
                            >
                                {sending
                                    ? appState.t("contact.form.sending")
                                    : appState.t("contact.form.submit")}
                            </button>
                        </form>
                    )}
                </div>
            )}
        </PublicPageShell>
    );
});
